import { Link, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { fetchEstablishmentDetails } from '../../services/announcesEstablishment'
import { User, GalleryItem } from '../interfaces/user'
import formatBelgianPhoneNumber from '../fonctions/formatBelgiumPhone'
import { Establishment, PracticeCategory } from '../interfaces/establishment'
import Modal from '../components/MyModal'
import { t } from 'i18next'
import { FaPenFancy, FaPhone, FaSms, FaWhatsapp } from 'react-icons/fa'
import {
  IoIosWarning,
  IoMdArrowDropleftCircle,
  IoMdArrowDroprightCircle,
  IoMdMail,
  IoMdRefresh,
} from 'react-icons/io'
import { UserService } from '../../services/user'
import { AiFillHome } from 'react-icons/ai'
import {
  MdCheckCircle,
  MdOutlineKeyboardDoubleArrowRight,
} from 'react-icons/md'
import { FaLocationDot } from 'react-icons/fa6'
import Gallery from '../components/Gallery'
import { API_BASE_URL, X_API_KEY } from '../../services/api'
import { updateSearchFiltersAndRedirect } from '../fonctions/updateSearchFiltersAndRedirect'
import {
  hasPracticesWarnings,
  PracticesWarnings,
} from '../components/PracticesWarnings'
import SendMailModal from '../components/SendMailModal'
import calculateAge from '../fonctions/calculateAge'
import ReportUserModal from '../components/ReportUserModal'

const dayMap: { [key: string]: number } = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 0,
}

function OneEstablishment() {
  const { id } = useParams<{ title: string; id: string }>()
  const [establishment, setEstablishment] = useState<Establishment | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [gallery, setGallery] = useState<GalleryItem[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [photoIndex, setPhotoIndex] = useState(0)
  const getPreviousSlug = () => {
    if (!establishment) return ''
    return formatURL(establishment.title, Number(establishment.id) - 1)
  }

  const getNextSlug = () => {
    if (!establishment) return ''
    return formatURL(establishment.title, Number(establishment.id) + 1)
  }
  const [practiceData, setPracticeData] = useState<PracticeCategory[]>([])
  const [, setPracticeNames] = useState<string[]>([])
  const [isSendMailModalOpen, setIsSendMailModalOpen] = useState(false)
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)
  const hasPrices =
    (establishment?.price?.length ?? 0) > 0 &&
    establishment?.timing?.length !== undefined &&
    establishment?.timing?.length > 0
  const getIdFromSlug = (slug: string | undefined): number | null => {
    if (!slug) return null
    const parts = slug.split('-')
    const encodedId = parts[parts.length - 1]
    try {
      const padding = '='.repeat((4 - (encodedId.length % 4)) % 4)
      const base64String = encodedId + padding
      const decodedId = atob(base64String)
      return parseInt(decodedId)
    } catch (error) {
      console.error('Error decoding slug:', error)
      return null
    }
  }

  const formatURL = (title: string, id: number) => {
    return `${title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')}/${id}`
  }
  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError('Invalid establishment ID')
        return
      }

      try {
        const data = await fetchEstablishmentDetails(id)
        if (data.establishment) {
          setEstablishment(data.establishment)
          setGallery(data.gallery || [])

          const userService = new UserService()
          const users = await userService.getAllUsers()
          const establishmentUser = users.find(
            u => u.id === data.establishment.userId,
          )

          if (establishmentUser) {
            setUser(establishmentUser)
          } else {
            console.error('User not found for establishment')
          }
        } else {
          setError('Establishment not found')
        }
      } catch (error) {
        setError('Failed to fetch data')
        console.error('Error fetching data:', error)
      }
    }

    fetchData()
  }, [id])

  useEffect(() => {
    const fetchPractices = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/practices`, {
          headers: { 'x-api-key': X_API_KEY || '' },
        })
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        const data = await response.json()
        setPracticeData(data.practiceList)

        if (establishment && establishment.practices) {
          const names = establishment.practices
            .map(id => {
              for (const category of data.practiceList) {
                const practice = category.practices.find(
                  (p: { id: number }) => p.id === id,
                )
                if (practice) {
                  return practice.practiceName
                }
              }
              return null
            })
            .filter((name): name is string => name !== null)
          setPracticeNames(names)
        }
      } catch (error) {
        console.error('Error fetching practices:', error)
      }
    }

    fetchPractices()
  }, [establishment])

  const handleLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    event.preventDefault()
    if (user?.address?.city) {
      updateSearchFiltersAndRedirect(user.address.city)
    }
  }

  const handlePrev = () => {
    setPhotoIndex((photoIndex + gallery.length - 1) % gallery.length)
  }

  const handleNext = () => {
    setPhotoIndex((photoIndex + 1) % gallery.length)
  }

  const isWithinTimeRange = (
    currentTime: string,
    start: string,
    end: string,
  ) => {
    const [currentHour, currentMin] = currentTime.split(':').map(Number)
    const [startHour, startMin] = start.split(':').map(Number)
    const [endHour, endMin] = end.split(':').map(Number)

    const current = currentHour * 60 + currentMin
    const startTime = startHour * 60 + startMin
    const endTime = endHour * 60 + endMin

    return current >= startTime && current <= endTime
  }
  const coverImage = gallery.find(item => item.cover === true)

  if (error) {
    return (
      <>
        <div className='bg-redpink'>
          <div className='max-w-7xl pb-2 mx-auto font-light'>
            <div className='flex pt-2'>
              <Link
                to='/'
                className='text-white hover:text-anthracite transition-all'
              >
                <AiFillHome className='text-2xl' />
              </Link>
              <MdOutlineKeyboardDoubleArrowRight className='mx-2 text-white text-2xl' />
              <span className='text-white'>{t('error')}</span>
            </div>
          </div>
        </div>
        <div className='max-w-7xl mx-auto py-10 text-center'>
          <div className='bg-grisclair p-8 rounded-lg shadow-md'>
            <IoIosWarning className='text-6xl text-redpink mx-auto mb-4' />
            <h2 className='text-2xl font-bold mb-4'>{t('error-occurred')}</h2>
            <p className='text-gray-600 mb-6'>{t('establishment-not-found')}</p>
            <div className='flex justify-center gap-4'>
              <Link
                to='/'
                className='bg-redpink text-white px-6 py-2 rounded hover:bg-anthracite transition-all inline-flex items-center'
              >
                <AiFillHome className='mr-2' />
                {t('dashboard.to-the-site')}
              </Link>
              <button
                onClick={() => window.location.reload()}
                className='bg-anthracite text-white px-6 py-2 rounded hover:bg-redpink transition-all inline-flex items-center'
              >
                <IoMdRefresh className='mr-2' />
                {t('reload-page')}
              </button>
            </div>
          </div>
        </div>
      </>
    )
  }

  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  return (
    <>
      <div className='bg-redpink'>
        <div className='max-w-7xl pb-2 mx-auto font-light flex justify-between '>
          <div className='flex pt-2'>
            <Link
              to='/'
              className='text-white hover:text-anthracite transition-all'
            >
              <AiFillHome className='text-2xl' />
            </Link>
            <MdOutlineKeyboardDoubleArrowRight className='mx-2 text-white text-2xl' />
            {Array.isArray(establishment?.status) &&
              establishment?.status.map((status, index) => (
                <div key={index} className='flex'>
                  <Link
                    to={`/establishment?status=${encodeURIComponent(status)}`}
                    className='text-white hover:text-anthracite transition-all'
                  >
                    {t(`create-ad.${status}`)}
                  </Link>
                  <MdOutlineKeyboardDoubleArrowRight className='mx-2 text-white text-2xl' />
                </div>
              ))}
            <p className='text-white'>{establishment?.title}</p>
          </div>
          <div className='flex'>
            <Link to={`/establishment/${getPreviousSlug()}`}>
              <IoMdArrowDropleftCircle className='text-4xl hover:text-white transition-all' />
            </Link>
            <Link to={`/establishment/${getNextSlug()}`}>
              <IoMdArrowDroprightCircle className='text-4xl hover:text-white transition-all' />
            </Link>
          </div>
        </div>
      </div>
      <div className='bg-anthracite'>
        <div className='max-w-7xl mx-auto font-light flex justify-between text-white py-3'>
          <div className='flex'>
            <FaPenFancy className='text-redpink text-xl mr-2' />
            {user && (
              <>
                {t('posted-by')}{' '}
                <Link
                  to={`/search/user/${user.id}`}
                  className='text-redpink pl-2 hover:underline transition-all'
                >
                  {user.nickname}
                </Link>
                {user.birthday && (
                  <span className='pl-2'>
                    ({calculateAge(user.birthday)} {t('years-old')})
                  </span>
                )}
                <Link
                  to='#'
                  className='text-redpink flex hover:underline ml-5'
                  onClick={e => {
                    e.preventDefault()
                    setIsReportModalOpen(true)
                  }}
                >
                  <IoIosWarning className='text-2xl mr-2' /> {t('report-user')}
                </Link>
              </>
            )}
          </div>
          <div className='flex'>
            {establishment?.emailOnly && (
              <Link
                to='#'
                className='flex hover:underline ml-3'
                onClick={e => {
                  e.preventDefault()
                  if (user?.email) {
                    setIsSendMailModalOpen(true)
                  }
                }}
              >
                <IoMdMail className='text-blue-500 mr-2 text-2xl' />
                {t('send-mail')}
              </Link>
            )}
            <>
              <a
                href={`tel:${user?.phone}`}
                className='flex hover:underline ml-4'
              >
                <FaPhone className='text-emerald-500 mr-2 text-xl' />
                {formatBelgianPhoneNumber(user?.phone ?? '')}
              </a>
              {establishment?.whatsapp && (
                <a
                  href={`https://wa.me/${user?.phone}`}
                  className='flex hover:underline ml-4'
                >
                  <FaWhatsapp className='text-redpink mr-2 text-xl' />
                  {formatBelgianPhoneNumber(user?.phone ?? '')}
                </a>
              )}
            </>
          </div>
        </div>
      </div>
      <div className='max-w-7xl mx-auto flex py-7'>
        {error ? (
          <p>{error}</p>
        ) : (
          establishment && (
            <>
              <div className='w-2/5'>
                <Gallery
                  gender={user?.userProfile?.gender}
                  gallery={gallery}
                  coverImage={coverImage}
                  statusToShow={[0, 1, 3, 13]}
                />
                {gallery.length > 1 && (
                  <div className='mt-4 flex flex-wrap gap-2 justify-between'>
                    {gallery.map((image, index) => (
                      <img
                        key={index}
                        src={image.fileUrl}
                        alt={`Image ${index + 1}`}
                        className='w-20 h-20 object-cover cursor-pointer rounded hover:opacity-80'
                        onClick={() => {
                          setCurrentImageIndex(index)
                          setIsGalleryModalOpen(true)
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className='w-3/5 ml-7'>
                <p className='text-3xl'>{establishment.title}</p>
                <div className='flex'>
                  {Array.isArray(establishment?.status)
                    ? establishment?.status.map((status, index) => (
                        <span key={index} className='text-redpink'>
                          <Link
                            to={`/establishment?status=${encodeURIComponent(
                              status,
                            )}`}
                            className='flex items-center justify-center bg-redpink text-white px-2 py-0 mr-2 mt-1 rounded hover:bg-anthracite transition-all'
                          >
                            {t(`create-ad.${status}`)}
                          </Link>
                        </span>
                      ))
                    : null}
                  <Link
                    className='flex bg-redpink text-white px-2 py-0 mr-2 mt-1 rounded hover:bg-anthracite transition-all'
                    to='/'
                    onClick={handleLinkClick}
                  >
                    <FaLocationDot className='mr-1 mt-1 text' />
                    {t(`${user?.address?.city}`)}
                  </Link>
                </div>
                {user?.address && (
                  <div className='bg-grisclair p-2 rounded-lg my-4 flex items-center'>
                    <FaLocationDot className='mr-2 text-redpink text-2xl' />
                    <span>
                      {user.address.street ? (
                        <>
                          {user.address.street} {user.address.number}
                          <br />
                        </>
                      ) : null}
                      {user.address.zip_code} {user.address.city}
                    </span>
                  </div>
                )}
                <p className='flex my-5 whitespace-pre-line'>
                  {establishment.description}
                </p>
                <div className='border border-1 border-anthracite overflow-hidden rounded-lg'>
                  <p className='text-lg bg-anthracite text-white rounded-t text-center py-1'>
                    {t('contact')}
                  </p>
                  <div className='flex'>
                    <div
                      className={`${
                        establishment.isAvailable ||
                        (Array.isArray(establishment.disponibility) &&
                          establishment.disponibility.length > 0)
                          ? 'w-1/2'
                          : 'w-full'
                      }`}
                    >
                      <div className='flex hover:bg-grisclair justify-between py-2  px-4'>
                        <a
                          href={`tel:${user?.phone}`}
                          className='flex justify-between w-full'
                        >
                          <span className='flex items-center'>
                            <FaPhone className='text-redpink mr-2 text-xl' />
                            <span>{t('phone')}</span>
                          </span>
                          <span className='text-redpink'>
                            {formatBelgianPhoneNumber(user?.phone ?? '')}
                          </span>
                        </a>
                      </div>
                      {establishment?.whatsapp && (
                        <div className='flex hover:bg-grisclair justify-between py-2  px-4'>
                          <a
                            href={`https://wa.me/${user?.phone}`}
                            className='flex justify-between w-full'
                          >
                            <span className='flex items-center'>
                              <FaWhatsapp className='text-whatsapp text-xl mr-2' />
                              <span>WhatsApp</span>
                            </span>
                            <span className='text-redpink'>
                              {formatBelgianPhoneNumber(user?.phone ?? '')}
                            </span>
                          </a>
                        </div>
                      )}

                      {establishment.website && (
                        <p className='hover:bg-grisclair flex justify-between py-2 px-4'>
                          <span>{t('website')} :</span>
                          <Link
                            to={
                              establishment.website.startsWith('http')
                                ? establishment.website
                                : `http://${establishment.website}`
                            }
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-redpink'
                          >
                            {establishment.website}
                          </Link>
                        </p>
                      )}
                      {establishment.callOnly && (
                        <p className='hover:bg-grisclair flex justify-center py-2 px-4'>
                          <span>
                            <FaPhone className='mr-2' />
                          </span>
                          {t('create-ad.call-only')}
                        </p>
                      )}
                      {establishment.smsOnly && (
                        <p className='hover:bg-grisclair flex justify-center py-2 px-4'>
                          <span>
                            <FaSms className='mr-2' />
                          </span>
                          {t('create-ad.sms-only')}
                        </p>
                      )}
                    </div>
                    {(establishment.isAvailable ||
                      (Array.isArray(establishment.disponibility) &&
                        establishment.disponibility.length > 0)) && (
                      <div className='w-1/2'>
                        {establishment.isAvailable ? (
                          <p className='hover:bg-grisclair flex justify-between py-2  px-4'>
                            <span className='text-center w-full'>
                              {t('create-ad.available247')}
                            </span>
                          </p>
                        ) : (
                          Array.isArray(establishment.disponibility) &&
                          establishment?.disponibility?.map(
                            (day: string, index: number) => {
                              const currentDay = new Date().getDay()
                              const isDayToday =
                                dayMap[day.toLowerCase()] === currentDay
                              const isOpen =
                                establishment.availabilityStart?.[index] !==
                                  '00:00' &&
                                establishment.availabilityEnd?.[index] !==
                                  '00:00'

                              return (
                                <p
                                  key={index}
                                  className='flex justify-between py-2  px-4 hover:bg-grisclair'
                                >
                                  <span
                                    className={`flex items-center ${
                                      isDayToday ? 'font-bold' : ''
                                    }`}
                                  >
                                    <span>
                                      {t(`create-ad.${day.toLowerCase()}`)}
                                    </span>
                                  </span>
                                  <span
                                    className={`text-redpink ${
                                      isOpen ? '' : 'text-gray-400'
                                    }`}
                                  >
                                    {establishment.availabilityStart &&
                                    establishment.availabilityEnd &&
                                    establishment.availabilityStart[index] ===
                                      '00:00' &&
                                    establishment.availabilityEnd[index] ===
                                      '00:00' ? (
                                      t('24h/24')
                                    ) : (
                                      <span className='flex'>
                                        {isDayToday && (
                                          <span className='text-green-500 mr-2 mt-0.5'>
                                            {isWithinTimeRange(
                                              new Date().toLocaleTimeString(
                                                'en-US',
                                                {
                                                  hour: '2-digit',
                                                  minute: '2-digit',
                                                  hour12: false,
                                                },
                                              ),
                                              establishment.availabilityStart?.[
                                                index
                                              ] || '',
                                              establishment.availabilityEnd?.[
                                                index
                                              ] || '',
                                            ) && <MdCheckCircle />}
                                          </span>
                                        )}
                                        {establishment.availabilityStart?.[
                                          index
                                        ] ?? ''}
                                        <span className='px-1'>{t('to')}</span>
                                        {establishment.availabilityEnd?.[
                                          index
                                        ] ?? ''}
                                      </span>
                                    )}
                                  </span>
                                </p>
                              )
                            },
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {establishment.practices &&
                  establishment.practices.length > 0 && (
                    <div className='border mt-5 border-1 border-anthracite rounded-lg pb-2'>
                      <p className='text-lg bg-anthracite text-white rounded-t text-center py-1'>
                        {t('services')}
                      </p>
                      {practiceData.map(category => {
                        const filteredPractices = category.practices.filter(
                          practice =>
                            establishment.practices.includes(practice.id),
                        )
                        return filteredPractices.length > 0 ? (
                          <div className='p-2 pb-0' key={category.id}>
                            <div className='w-full text-center bg-grisclair2 rounded-lg mb-2 uppercase p-1'>
                              {t(`create-ad.${category.categoryName}`)}
                            </div>
                            <div className='w-full grid grid-cols-3 text-center'>
                              {filteredPractices.map(practice => (
                                <div
                                  className='relative px-2 py-3 text-sm font-semibold hover:bg-grisclair rounded-lg'
                                  key={practice.id}
                                >
                                  {t(`practices.${practice.practiceName}`)}
                                  {hasPracticesWarnings(
                                    practice.practiceName,
                                  ) && (
                                    <PracticesWarnings
                                      practiceName={practice.practiceName}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null
                      })}
                    </div>
                  )}
                {hasPrices && (
                  <div className='border mt-5 border-1 border-anthracite rounded-lg'>
                    <p className='text-lg bg-anthracite text-white rounded-t text-center py-1'>
                      {t('create-ad.prices')}
                    </p>
                    <div className='w-full grid grid-cols-2 gap-2 p-2   text-center'>
                      {establishment.timing.map((time, index) => (
                        <div
                          key={index}
                          className='w-full flex justify-center items-center'
                        >
                          <span className=' w-4/6 p-2 bg-grisclair hover:bg-grisclair2 rounded-l text-left'>
                            {t(`create-ad.${time}`)}
                          </span>
                          <span className='w-2/6 px-2 py-1.5 bg-redpink text-white rounded-r text-xl'>
                            {establishment.price[index]}€
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )
        )}
      </div>
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onPrev={handlePrev}
        onNext={handleNext}
      >
        {gallery.length > 0 && (
          <img
            src={gallery[photoIndex]?.fileUrl}
            alt={gallery[photoIndex]?.fileName}
            className='max-w-full max-h-[90vh] rounded-lg'
          />
        )}
      </Modal>
      {user?.email && (
        <SendMailModal
          isOpen={isSendMailModalOpen}
          onClose={() => setIsSendMailModalOpen(false)}
          recipientEmail={user.email}
          recipientName={user.nickname ?? ''}
        />
      )}
      {user && (
        <ReportUserModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          userNickname={user.nickname}
          userId={user.id}
        />
      )}
      {isGalleryModalOpen && (
        <div className='fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center'>
          <button
            className='absolute top-5 right-5 text-white text-3xl'
            onClick={() => setIsGalleryModalOpen(false)}
          >
            &times;
          </button>

          <button
            onClick={() =>
              setCurrentImageIndex(prev =>
                prev === 0 ? gallery.length - 1 : prev - 1,
              )
            }
            className='absolute left-4 text-white text-4xl'
          >
            &#10094;
          </button>

          <img
            src={gallery[currentImageIndex].fileUrl}
            alt={`Image ${currentImageIndex + 1}`}
            className='max-w-[90%] max-h-[90%] rounded-lg shadow-lg'
          />

          <button
            onClick={() =>
              setCurrentImageIndex(prev =>
                prev === gallery.length - 1 ? 0 : prev + 1,
              )
            }
            className='absolute right-4 text-white text-4xl'
          >
            &#10095;
          </button>
        </div>
      )}
    </>
  )
}

export default OneEstablishment
